import { EventEmitter, Injectable, Output } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CNItem } from '../models/cnitem';
import { CNPatient } from '../models/cnpatient';
import { SNItemFactory } from '../models/snitemfactory';
import { BecareService } from './becare.service';
import { PatientService } from './patient.service';

export enum RecordServiceFilter {
  all = 'all',
  active = 'active',
  notactive = 'notactive'
}

export class RecordServiceSubject {
  private becareService: BecareService;
  private myPatient: CNPatient;
  private myName: string;
  private myFilter: RecordServiceFilter;
  private paged: boolean | number;
  private buffer: CNItem[];
  public records: BehaviorSubject<CNItem[]>;
  private myStart: number;
  private myLimit: number;
  public length: number;

  private recordUrl = '';

  constructor(becare: BecareService, patient: CNPatient, name: string, filter: RecordServiceFilter, paged: boolean | number) {
    this.becareService = becare;
    this.myName = name;
    this.myFilter = filter;
    this.paged = paged;
    if (paged) {
      this.myStart = 0;
      this.myLimit = (typeof paged === 'number') ? paged : 0; // No limit if not paged
      this.length = 0;
    }
    this.records = new BehaviorSubject<CNItem[]>([]);
    // Set patient, build rest url and query first buffer
    this.patient = patient;
  }

  private load() {
    if (!this.recordUrl) {
      return;
    }
    this.becareService.call(this.recordUrl).subscribe(result => {
      if (result.success === true && result.records !== undefined) {
        const factory = new SNItemFactory();
        this.buffer = factory.itemsFromJson(result.records);
        this.length = result.total;
        this.records.next(this.buffer);
      }
    });
  }

  private buildUrl() {
    if (this.paged) {
      this.recordUrl = '/fr/patient/' + this.myPatient.id + '/records?start=' + this.myStart + '&limit=' + this.myLimit;
    } else {
      switch (this.myFilter) {
        default:
          this.recordUrl = '';
      }
    }
  }

  set patient(patient: CNPatient) {
    this.myPatient = patient;
    if (!this.myPatient) {
      return;
    }
    this.buildUrl();
    this.load();
  }

  set start(start: number) {
    this.myStart = start;
    this.buildUrl();
    this.load();
  }

  get start(): number {
    return this.myStart;
  }

  set limit(limit: number) {
    this.myLimit = limit;
    this.buildUrl();
    this.load();
  }
}

@Injectable({
  providedIn: 'root'
})
export class RecordService {

  private static instance: RecordService = null;
  private currentpatient: CNPatient;
  @Output() clear = new EventEmitter<boolean>();

  private subjects: Map<string, RecordServiceSubject> = new Map();

  constructor(private becare: BecareService, private patient: PatientService) {
    RecordService.instance = this;
    this.patient.patientSelectedEvent.subscribe(patientselected => {
      this.currentpatient = patientselected;
      this.subjects.forEach((subject: RecordServiceSubject, name: string) => {
        subject.patient = this.currentpatient;
      });
      this.clear.emit(true);
    });
    // Initiate with current selected patient
    this.currentpatient = this.patient.patient;
  }

  public static getInstance(): RecordService {
    return RecordService.instance;
  }

  public getSubject(name: string, filter?: RecordServiceFilter, paged?: boolean | number): RecordServiceSubject {
    if (this.subjects.has(name)) {
      return this.subjects.get(name);
    }
    if (!filter && !paged) {
      return undefined;
    }
    // Build new service subject
    const newSubject = new RecordServiceSubject(this.becare, this.currentpatient, name, filter, paged);
    this.subjects.set(name, newSubject);
    return newSubject;
  }
}
